/**
 * Coins AileFX added to a network at run time, and coins it removed. A removed
 * coin stays on Treasury and Limits while the treasury holds or approves it or
 * its daily cap is not zero, so it can be lowered or revoked.
 */

import type { PairToken, TokenOrigin } from "./api";

/** A pair coin's origin: a coin that names none comes from an older Maker portal API, and is the deployment's. */
export function originOf(token: Pick<PairToken, "origin">): TokenOrigin {
  return token.origin === "console" ? "console" : "deployment";
}

/** Whether the network still offers a Treasury or Limits coin: only an explicit `offered: false` says no (an older API says nothing). */
export function isOffered(token: { offered?: boolean | undefined }): boolean {
  return token.offered !== false;
}

/** The line of a coin AileFX no longer offers, whose row stays editable. */
export function noLongerOfferedLine(symbol: string): string {
  return `AileFX no longer offers ${symbol} here. You can still lower its cap and revoke its allowance.`;
}

/** The coins AileFX added among a pair's two, base first: the coins a `token-changed` may be about. */
export function addedCoins(base: Pick<PairToken, "symbol" | "origin">, quote: Pick<PairToken, "symbol" | "origin">): string[] {
  return [base, quote].filter((t) => originOf(t) === "console").map((t) => t.symbol);
}

/** The reason `token-changed` does not say which coin, so a pair of two added coins names both. */
export function tokenChangedWords(coins: readonly string[]): string {
  const which = coins.length === 0 ? "a coin of this pair" : coins.join(" or ");
  return `AileFX’s record of ${which} no longer matches the chain. AileFX must add it again.`;
}
